import './global.css';
import React, { useEffect, type ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { StripeProvider } from './src/services/stripe';
import { RootNavigator } from './src/navigation/RootNavigator';
import { PaywallModal } from './src/components/PaywallModal';
import { UpdateBanner } from './src/components/UpdateBanner';
import { UpdateRequiredScreen } from './src/screens/UpdateRequiredScreen';
import { useAuth } from './src/hooks/useAuth';
import { useAuthDeepLink } from './src/hooks/useAuthDeepLink';
import { useRevenueCat } from './src/hooks/useRevenueCat';
import { useVersionGate } from './src/hooks/useVersionGate';
import { STRIPE_PUBLISHABLE_KEY, verifierConfigurationStripe } from './src/config/stripe';
import { useLanguageStore } from './src/store/languageStore';
import { useNouveautesStore } from './src/store/nouveautesStore';
import { RacineSens } from './src/components/RacineSens';
import { initSentry } from './src/services/sentry';
import * as WebBrowser from 'expo-web-browser';
import { SUR_LE_WEB } from './src/theme/tokens';

/**
 * AU CHARGEMENT DU MODULE, avant tout rendu : les gestionnaires de crash doivent être posés avant
 * le premier code susceptible de planter. Un appel dans un `useEffect` arriverait après le montage,
 * donc trop tard pour le seul incident qu'on ne peut pas se permettre de rater — celui du démarrage.
 *
 * `Sentry.wrap(App)` n'est volontairement PAS utilisé : il installe un `TouchEventBoundary` qui
 * trace ce que l'utilisateur touche. C'est du contenu d'écran, et ce projet n'en envoie pas.
 */
initSentry();
// APRÈS `initSentry`, jamais avant : le garde-fou remonte son verdict à Sentry, et l'ordre inverse
// le rendrait muet précisément dans le build où il doit parler.
verifierConfigurationStripe();

/**
 * RETOUR DE LA CONNEXION GOOGLE SUR LE WEB — sans cette ligne, la connexion échoue EN SILENCE.
 *
 * CE QUI SE PASSE RÉELLEMENT. `useAuth` n'utilise pas une redirection de page mais
 * `WebBrowser.openAuthSessionAsync`, qui sur le web ouvre une POPUP (`window.open`). Le retour de
 * Google atterrit donc dans cette popup, à `/auth/callback?code=…`, et jamais dans l'onglet
 * principal — dont la barre d'adresse ne porte à aucun moment le code.
 *
 * Pour que l'onglet parent l'apprenne, c'est l'application CHARGÉE DANS LA POPUP qui doit appeler
 * `maybeCompleteAuthSession()` : la fonction compare l'URL courante à l'URL de redirection
 * mémorisée, puis `postMessage` au parent, ce qui résout enfin sa promesse en `success`. C'est
 * cette ligne-ci qui s'exécute dans la popup, puisqu'elle y charge le même bundle.
 *
 * SANS ELLE : aucun message, la promesse du parent n'est résolue qu'à la fermeture de la popup et
 * vaut `dismiss`, que `useAuth` interprète — légitimement, pour le mobile — comme une annulation
 * utilisateur et avale sans rien afficher. Symptôme mesuré le 18/09/2026 : retour à l'écran de
 * départ, aucune session, aucune erreur, et le vérifieur PKCE laissé intact dans `localStorage`
 * puisque `exchangeCodeForSession` n'était jamais atteint.
 *
 * La règle de réécriture (`public/_redirects`) en est le PRÉALABLE : sans elle la popup n'obtient
 * qu'un 404 sur `/auth/callback` et aucun code de l'application ne s'y exécute.
 *
 * Gardée par `SUR_LE_WEB` : sur mobile le retour passe par `useAuthDeepLink` et le schéma natif,
 * chemin qu'on ne touche pas.
 */
if (SUR_LE_WEB) WebBrowser.maybeCompleteAuthSession();

// Monte l'écoute de session Supabase + l'init RevenueCat (achats liés au compte) au-dessus de la navigation.
function AuthGate({ children }: { children: ReactNode }) {
  useAuth();
  useAuthDeepLink();
  useRevenueCat();
  return <>{children}</>;
}

export default function App() {
  // Langue d'interface : préférence enregistrée, sinon celle de l'appareil. On attend la lecture
  // avant le premier rendu — sinon l'écran s'afficherait en français puis basculerait.
  const { language, ready, load } = useLanguageStore();

  // Nouveautés : la lecture des marqueurs d'une installation antérieure part AVANT tout le reste,
  // et surtout avant `useVersionGate`, qui réécrit l'un d'eux (services/nouveautes.ts).
  const preparerNouveautes = useNouveautesStore((s) => s.preparer);
  useEffect(() => {
    void preparerNouveautes();
  }, [preparerNouveautes]);

  /**
   * LE GATE NE RETARDE JAMAIS LE PREMIER RENDU. Aucun `await` n'est ajouté sur le chemin de
   * démarrage : le hook part de `inconnu`, l'application se monte exactement comme avant, et le
   * verdict arrive plus tard s'il arrive. Réseau coupé ou Supabase indisponible : le verdict reste
   * `inconnu`, rien ne s'affiche, et l'application s'ouvre normalement dans le métro.
   */
  const { verdict, storeUrl, banniereVisible, ignorerBanniere } = useVersionGate();

  useEffect(() => {
    void load();
  }, [load]);

  if (!ready) return null;

  /**
   * ÉCRAN BLOQUANT — RETOUR ANTICIPÉ, ET C'EST LE POINT ESSENTIEL DU BRANCHEMENT.
   *
   * Il ne se contente pas d'être « au-dessus » : il rend le reste INEXISTANT. Ni `RootNavigator`,
   * ni `PaywallModal`, ni `AuthGate`. Trois conséquences voulues :
   *
   * — RIEN NE PEUT LE MASQUER. `PaywallModal` est un `Modal` React Native, donc une hiérarchie
   *   NATIVE séparée qui se peint au-dessus de tout l'arbre : rendu en frère, il recouvrirait
   *   l'écran bloquant. Non monté, la question ne se pose plus.
   * — IL N'EXIGE AUCUNE SESSION. `AuthGate` n'est pas monté, donc aucun hook d'authentification, de
   *   deep link ou de RevenueCat ne tourne. L'écran peut s'afficher avant toute connexion — ce qui
   *   est précisément le cas où il doit fonctionner.
   * — IL NE DÉPEND QUE DE `SafeAreaProvider`. Pas de Stripe, pas de navigation, pas de store.
   *
   * `storeUrl` est exigé : sans destination, le bouton n'irait nulle part et l'utilisateur serait
   * enfermé sans issue. Pas d'URL, pas de blocage — la même règle de sûreté que partout ici.
   */
  if (verdict === 'bloque' && storeUrl) {
    return (
      <SafeAreaProvider>
        <UpdateRequiredScreen storeUrl={storeUrl} />
        <StatusBar style="auto" />
      </SafeAreaProvider>
    );
  }

  return (
    // StripeProvider au root : carte uniquement pour l'instant. Apple Pay s'ajoutera ici
    // via merchantIdentifier (compte développeur Apple requis) sans refonte.
    <StripeProvider publishableKey={STRIPE_PUBLISHABLE_KEY}>
      <SafeAreaProvider>
        <RacineSens>
        <AuthGate>
          {/* CLÉ de langue : changer de langue re-monte l'arbre entier, aucun fragment ne peut
              rester dans l'ancienne. */}
          <RootNavigator key={language} />
        </AuthGate>

        {/* BANNIÈRE EN SURIMPRESSION, pas en flux. Insérée comme frère au-dessus du navigateur, elle
            décalerait tout l'écran vers le bas — et les écrans en dessous, qui appliquent déjà leur
            propre marge de zone sûre, en ajouteraient une seconde. En absolu, la mise en page du
            navigateur reste rigoureusement celle d'aujourd'hui. `box-none` laisse passer les appuis
            en dehors de la bannière elle-même. */}
        {banniereVisible && storeUrl ? (
          <SafeAreaView edges={['top']} style={styles.banniere} pointerEvents="box-none">
            <UpdateBanner storeUrl={storeUrl} onDismiss={ignorerBanniere} />
          </SafeAreaView>
        ) : null}

        <PaywallModal />
        </RacineSens>
        <StatusBar style="auto" />
      </SafeAreaProvider>
    </StripeProvider>
  );
}

const styles = StyleSheet.create({
  banniere: { position: 'absolute', top: 0, left: 0, right: 0 },
});
